import { useAuth } from "@clerk/react";
import { useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { RealtimeClient, socketUrl } from "@luckymenu/realtime-client";
import { useRealtimeStatus } from "@luckymenu/realtime-client/react";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";

/**
 * AI job progress in the admin app (P8-T05). One socket per tab, as a
 * platform admin (`adminAuth`, channel `a:platform`). Every `ai.job.updated`
 * refetches the open AI queries; while the socket is down, screens with a
 * running job poll every 5 seconds instead (`useJobPolling`).
 */

const SocketOpen = createContext(false);

export function AiRealtimeProvider({ children }: { children: ReactNode }) {
	const { getToken } = useAuth();
	const tokenRef = useRef(getToken);
	tokenRef.current = getToken;
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [client] = useState(
		() =>
			new RealtimeClient({
				url: socketUrl(window.location.origin),
				getAuth: async () => {
					const token = await tokenRef.current();
					return token ? { token, admin: true } : null;
				},
			}),
	);
	const status = useRealtimeStatus(client);

	useEffect(() => {
		const aiKey = trpc.admin.ai.pathKey();
		const refetch = () => void queryClient.invalidateQueries({ queryKey: aiKey });
		const offEvent = client.onEvent((event) => {
			// A finished site job is picked up by the editor's AI panel, which reloads the draft.
			if (event.type === "ai.job.updated") refetch();
		});
		const offReconnect = client.onReconnect(refetch);
		client.start();
		return () => {
			offEvent();
			offReconnect();
			client.stop();
		};
	}, [client, queryClient, trpc]);

	return <SocketOpen value={status.state === "open"}>{children}</SocketOpen>;
}

/** `refetchInterval` for a query that follows a running job: none while the socket is open. */
export function useJobPolling(running: boolean): number | false {
	const open = useContext(SocketOpen);
	return running && !open ? 5000 : false;
}
